import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteField,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { preMeetingEventId, sharedAgendaItemVisibleOnDashboard } from '../../../lib/sharedAgendaVisibility';
import type { AgendaItem } from '../../../types/sharedAgenda.types';

function linkifyAgendaTitle(text: string): React.ReactNode {
  const re = /(https?:\/\/[^\s<>"']+)/gi;
  const parts = text.split(re);
  return parts.map((part, i) =>
    /^https?:\/\//i.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noreferrer"
        style={{ color: '#0A6EBD', textDecoration: 'underline' }}
        onClick={(e) => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

export interface AgendaRosterMember {
  id: string;
  name: string;
  photoURL?: string | null;
}

type TokenSet = {
  bg: string;
  white: string;
  text: string;
  textMuted: string;
  textSub: string;
  border: string;
  accent: string;
  accentLight: string;
  accentBg: string;
};

type Props = {
  entityId: string;
  userId: string;
  userName: string;
  userPhotoURL?: string | null;
  members: AgendaRosterMember[];
  canDeleteOthers?: boolean;
  colors: TokenSet;
};

export const PreMeetingAgendaPointsPanel: React.FC<Props> = ({
  entityId,
  userId,
  userName,
  userPhotoURL,
  members,
  canDeleteOthers,
  colors: T,
}) => {
  const [items, setItems] = useState<AgendaItem[]>([]);
  const [draft, setDraft] = useState('');
  const [filterUid, setFilterUid] = useState<string | null>(null);
  const [listenerError, setListenerError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [deleteUndo, setDeleteUndo] = useState<{ id: string; title: string } | null>(null);
  const deleteUndoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDeleteUndoTimer = useCallback(() => {
    if (deleteUndoTimerRef.current) {
      clearTimeout(deleteUndoTimerRef.current);
      deleteUndoTimerRef.current = null;
    }
  }, []);

  const scheduleDeleteUndoClear = useCallback(() => {
    clearDeleteUndoTimer();
    deleteUndoTimerRef.current = setTimeout(() => {
      setDeleteUndo(null);
      deleteUndoTimerRef.current = null;
    }, 8000);
  }, [clearDeleteUndoTimer]);

  useEffect(() => () => clearDeleteUndoTimer(), [clearDeleteUndoTimer]);

  useEffect(() => {
    if (!entityId) return;
    const q = query(collection(db, 'sharedAgendaItems'), where('entityId', '==', entityId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setListenerError(null);
        const rows: AgendaItem[] = snap.docs
          .map((d) => ({ id: d.id, data: d.data() as Record<string, unknown> }))
          .filter(({ data }) => sharedAgendaItemVisibleOnDashboard(data, entityId))
          .map(({ id, data }) => ({
            id,
            eventId: (data.eventId as string) ?? preMeetingEventId(entityId),
            entityId: (data.entityId as string) ?? entityId,
            title: (data.title as string) ?? '',
            description: data.description as string | undefined,
            type: (data.type as AgendaItem['type']) ?? 'action',
            status: (data.status as AgendaItem['status']) ?? 'planned',
            order: typeof data.order === 'number' ? data.order : 0,
            submittedByUid: data.submittedByUid as string | undefined,
            submittedByName: data.submittedByName as string | undefined,
            submittedByAvatar: (data.submittedByAvatar as string | null) ?? null,
            meetingCheckedAt:
              data.meetingCheckedAt && typeof (data.meetingCheckedAt as Timestamp).toDate === 'function'
                ? (data.meetingCheckedAt as Timestamp).toDate()
                : undefined,
            createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
          }))
          .sort((a, b) => a.order - b.order || a.createdAt.getTime() - b.createdAt.getTime());
        setItems(rows);
      },
      (err) => {
        console.error('[PreMeeting agenda] onSnapshot:', err);
        setListenerError('Agenda punten kunnen niet live laden.');
      },
    );
    return () => unsub();
  }, [entityId]);

  const submitters = useMemo(() => {
    const m = new Map<string, { uid: string; name: string; avatar: string | null }>();
    for (const it of items) {
      const uid = it.submittedByUid;
      if (!uid) continue;
      if (!m.has(uid)) m.set(uid, { uid, name: it.submittedByName || 'Lid', avatar: it.submittedByAvatar ?? null });
    }
    return [...m.values()];
  }, [items]);

  const rosterById = useMemo(() => {
    const x = new Map<string, AgendaRosterMember>();
    members.forEach((mem) => x.set(mem.id, mem));
    return x;
  }, [members]);

  const openItems = useMemo(() => items.filter((i) => !i.meetingCheckedAt), [items]);

  const myCheckedItems = useMemo(() => {
    return items
      .filter((i) => i.submittedByUid === userId && i.meetingCheckedAt)
      .sort((a, b) => (b.meetingCheckedAt!.getTime() - a.meetingCheckedAt!.getTime()));
  }, [items, userId]);

  const displayed = useMemo(() => {
    if (!filterUid) return openItems;
    return openItems.filter((i) => i.submittedByUid === filterUid);
  }, [openItems, filterUid]);

  const addItem = useCallback(async () => {
    const title = draft.trim();
    if (!title) {
      setSaveError('Vul een titel in.');
      return;
    }
    if (!entityId || !userId) return;
    setSaveError(null);
    try {
      const nextOrder = openItems.reduce((mx, it) => Math.max(mx, typeof it.order === 'number' ? it.order : 0), 0) + 1;
      await addDoc(collection(db, 'sharedAgendaItems'), {
        eventId: preMeetingEventId(entityId),
        entityId,
        title,
        type: 'action',
        status: 'planned',
        order: nextOrder,
        submittedByUid: userId,
        submittedByName: userName,
        submittedByAvatar: userPhotoURL ?? null,
        preMeetingEntity: true,
        meetingRoom: null,
        createdAt: serverTimestamp(),
      });
      setDraft('');
    } catch (e: unknown) {
      console.error(e);
      const code = e && typeof e === 'object' && 'code' in e ? String((e as { code: string }).code) : '';
      setSaveError(code === 'permission-denied' ? 'Geen rechten om op te slaan.' : 'Opslaan mislukt. Probeer opnieuw.');
    }
  }, [draft, entityId, userId, userName, userPhotoURL, openItems]);

  const markChecked = useCallback(
    async (item: AgendaItem) => {
      if (item.submittedByUid !== userId || item.meetingCheckedAt) return;
      try {
        await updateDoc(doc(db, 'sharedAgendaItems', item.id), { meetingCheckedAt: serverTimestamp() });
      } catch (err) {
        console.error(err);
      }
    },
    [userId],
  );

  const undoChecked = useCallback(
    async (itemId: string) => {
      const it = items.find((i) => i.id === itemId);
      if (!it || it.submittedByUid !== userId || !it.meetingCheckedAt) return;
      try {
        await updateDoc(doc(db, 'sharedAgendaItems', itemId), { meetingCheckedAt: deleteField() });
      } catch (err) {
        console.error(err);
      }
    },
    [userId, items],
  );

  const saveTitleEdit = useCallback(async () => {
    if (!editingId) return;
    const t = editDraft.trim();
    if (!t) {
      setEditingId(null);
      return;
    }
    try {
      await updateDoc(doc(db, 'sharedAgendaItems', editingId), { title: t, updatedAt: serverTimestamp() });
    } catch (err) {
      console.error(err);
    }
    setEditingId(null);
  }, [editingId, editDraft]);

  const softRemove = useCallback(
    async (item: AgendaItem) => {
      const own = item.submittedByUid === userId;
      if (!own && !canDeleteOthers) return;
      try {
        await updateDoc(doc(db, 'sharedAgendaItems', item.id), { deletedAt: serverTimestamp() });
        setDeleteUndo({ id: item.id, title: item.title });
        scheduleDeleteUndoClear();
      } catch (err) {
        console.error(err);
      }
    },
    [userId, canDeleteOthers, scheduleDeleteUndoClear],
  );

  const undoSoftRemove = useCallback(async () => {
    if (!deleteUndo) return;
    try {
      await updateDoc(doc(db, 'sharedAgendaItems', deleteUndo.id), { deletedAt: deleteField() });
      clearDeleteUndoTimer();
      setDeleteUndo(null);
    } catch (err) {
      console.error(err);
    }
  }, [deleteUndo, clearDeleteUndoTimer]);

  const resolveAvatar = (uid: string, fallback: string | null, name: string) => {
    const mem = rosterById.get(uid);
    const url = fallback ?? mem?.photoURL ?? null;
    const initial = (name || 'L').charAt(0).toUpperCase();
    return { url, initial };
  };

  return (
    <div style={{ marginTop: 2, marginBottom: 12 }}>
      <div style={{ fontSize: 10.5, fontWeight: 800, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
        Agenda punten
      </div>
      <div style={{ fontSize: 11, color: T.textSub, marginBottom: 10, lineHeight: 1.45 }}>
        Punten voor de volgende sessie — live zichtbaar in de meeting voor iedereen in deze groep.
      </div>
      {listenerError && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '8px 10px', fontSize: 11, color: '#92400E', marginBottom: 8 }}>
          {listenerError}
        </div>
      )}
      {saveError && (
        <div style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 10px', fontSize: 11, color: '#B91C1C', marginBottom: 8 }}>
          {saveError}
        </div>
      )}
      {deleteUndo && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            background: T.accentLight,
            border: `1px solid ${T.accent}`,
            borderRadius: 8,
            padding: '8px 10px',
            fontSize: 11,
            color: T.textSub,
            marginBottom: 8,
          }}
        >
          <span style={{ minWidth: 0 }}>Verwijderd: «{deleteUndo.title.length > 42 ? `${deleteUndo.title.slice(0, 40)}…` : deleteUndo.title}»</span>
          <button
            type="button"
            onClick={() => void undoSoftRemove()}
            style={{
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 700,
              color: T.accent,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Ongedaan
          </button>
        </div>
      )}
      {submitters.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <button
            type="button"
            onClick={() => setFilterUid(null)}
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 20,
              border: `1px solid ${filterUid === null ? T.accent : T.border}`,
              background: filterUid === null ? T.accentLight : T.white,
              color: T.textSub,
              cursor: 'pointer',
            }}
          >
            Iedereen
          </button>
          {submitters.map((s) => {
            const { url, initial } = resolveAvatar(s.uid, s.avatar, s.name);
            const active = filterUid === s.uid;
            return (
              <button
                key={s.uid}
                type="button"
                title={s.name}
                onClick={() => setFilterUid((prev) => (prev === s.uid ? null : s.uid))}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  padding: 0,
                  border: active ? `2px solid ${T.accent}` : `1px solid ${T.border}`,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: T.accentBg,
                  flexShrink: 0,
                }}
              >
                {url ? (
                  <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <span style={{ fontSize: 11, fontWeight: 800, color: T.accent }}>{initial}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {displayed.length === 0 ? (
          <div style={{ fontSize: 11.5, color: T.textMuted, padding: '8px 0' }}>
            {openItems.length === 0 && items.length > 0 && !filterUid
              ? 'Geen openstaande punten — afgevinkte punten staan hieronder.'
              : `Nog geen agenda punten${filterUid ? ' van deze persoon' : ''}.`}
          </div>
        ) : (
          displayed.map((item) => {
            const own = item.submittedByUid === userId;
            const del = own || canDeleteOthers;
            const canEdit = own || !!canDeleteOthers;
            const uid = item.submittedByUid || '';
            const av = resolveAvatar(uid, item.submittedByAvatar ?? null, item.submittedByName || '');
            const isEditing = editingId === item.id;
            return (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '8px 10px',
                  borderRadius: 10,
                  background: T.bg,
                  border: `1px solid ${T.border}`,
                }}
              >
                {own ? (
                  <button
                    type="button"
                    title="Afvinken"
                    aria-label="Afvinken"
                    onClick={() => void markChecked(item)}
                    style={{
                      width: 22,
                      height: 22,
                      marginTop: 3,
                      borderRadius: 5,
                      padding: 0,
                      border: `1.5px solid ${T.accent}`,
                      background: T.white,
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div style={{ width: 22, flexShrink: 0, marginTop: 3 }} aria-hidden />
                )}
                <button
                  type="button"
                  title="Alleen punten van deze persoon"
                  onClick={() => {
                    if (!item.submittedByUid) return;
                    setFilterUid((prev) => (prev === item.submittedByUid ? null : item.submittedByUid!));
                  }}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    padding: 0,
                    border: `1px solid ${T.border}`,
                    overflow: 'hidden',
                    cursor: item.submittedByUid ? 'pointer' : 'default',
                    background: T.accentBg,
                    flexShrink: 0,
                  }}
                >
                  {av.url ? (
                    <img src={av.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ fontSize: 10, fontWeight: 800, color: T.accent, lineHeight: '26px', display: 'block' }}>{av.initial}</span>
                  )}
                </button>
                <div style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: T.text, lineHeight: 1.35 }}>
                  {isEditing ? (
                    <input
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      onBlur={() => void saveTitleEdit()}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void saveTitleEdit();
                        if (e.key === 'Escape') {
                          setEditingId(null);
                        }
                      }}
                      autoFocus
                      style={{
                        width: '100%',
                        fontSize: 12.5,
                        color: T.text,
                        border: `1px solid ${T.accent}`,
                        borderRadius: 6,
                        padding: '4px 6px',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  ) : (
                    linkifyAgendaTitle(item.title)
                  )}
                </div>
                {canEdit && !isEditing && (
                  <button
                    type="button"
                    title="Tekst wijzigen"
                    aria-label="Tekst wijzigen"
                    onClick={() => {
                      setEditingId(item.id);
                      setEditDraft(item.title);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: T.textMuted,
                      cursor: 'pointer',
                      padding: 2,
                      fontSize: 13,
                      flexShrink: 0,
                    }}
                  >
                    ✎
                  </button>
                )}
                {del && (
                  <button
                    type="button"
                    onClick={() => void softRemove(item)}
                    style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', padding: 2, fontSize: 14, flexShrink: 0 }}
                    aria-label="Verwijderen"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
      {myCheckedItems.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
            Mijn afgevinkte punten
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {myCheckedItems.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '6px 10px',
                  borderRadius: 8,
                  background: T.accentBg,
                  border: `1px solid ${T.border}`,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: T.textMuted,
                    textDecoration: 'line-through',
                    minWidth: 0,
                    flex: 1,
                    lineHeight: 1.35,
                  }}
                >
                  {item.title}
                </span>
                <button
                  type="button"
                  onClick={() => void undoChecked(item.id)}
                  style={{
                    flexShrink: 0,
                    fontSize: 10,
                    fontWeight: 700,
                    color: T.accent,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Ongedaan
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, border: `1px solid ${T.border}`, background: T.white }}>
        {userPhotoURL ? (
          <img src={userPhotoURL} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
        ) : (
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: T.accentBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 800,
              color: T.accent,
            }}
          >
            {userName.charAt(0).toUpperCase()}
          </div>
        )}
        <input
          value={draft}
          onChange={(e) => {
            setSaveError(null);
            setDraft(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) void addItem();
          }}
          placeholder="Agendapunt voor volgende meeting…"
          style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 13, color: T.text }}
        />
        <button
          type="button"
          onClick={() => void addItem()}
          disabled={!draft.trim()}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: 'none',
            background: draft.trim() ? T.accent : T.border,
            color: '#fff',
            fontSize: 18,
            fontWeight: 700,
            cursor: draft.trim() ? 'pointer' : 'default',
            opacity: draft.trim() ? 1 : 0.5,
          }}
        >
          +
        </button>
      </div>
    </div>
  );
};
